import React from 'react';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import { Listing, storeBadge, storeCtaLabel } from '../lib/listings';
import { getBrandImageUrl } from '../lib/brandImages';
import { thumbUrl } from '../lib/images';
import LikeButton from './LikeButton';

interface SearchResultsProps {
  listings: Listing[];
  loading: boolean;
  onClearFilters?: () => void;
}

function SearchResults({ listings, loading, onClearFilters }: SearchResultsProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg shadow-sm p-4 animate-pulse">
            <div className="aspect-square bg-gray-200 rounded-lg mb-4" />
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (listings.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-4">No listings found matching your search criteria.</p>
        {onClearFilters && (
          <button
            onClick={onClearFilters}
            className="px-4 py-2 text-sm font-medium rounded-md bg-rose-600 text-white hover:bg-rose-700"
          >
            Clear all filters
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {listings.map((listing) => {
        const isStore = listing.source === 'clearancewigs';
        const badge = storeBadge(listing);
        return (
        <Link
          key={listing.id}
          to={`/listing/${listing.slug || listing.id}`}
          className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
        >
          <div className="relative pb-[100%]">
            <img
              src={thumbUrl(listing.image_urls?.[0], 600) || getBrandImageUrl(listing.brand)}
              alt={listing.title}
              loading="lazy"
              decoding="async"
              onError={(e) => {
                const fallback = getBrandImageUrl(listing.brand);
                if (e.currentTarget.src !== fallback) e.currentTarget.src = fallback;
              }}
              className="absolute inset-0 w-full h-full object-cover"
            />
            {badge && (
              <div className="absolute top-2 left-2 bg-rose-600 text-white text-[11px] font-semibold px-2 py-0.5 rounded-full shadow">
                {badge}
              </div>
            )}
            {!isStore && (
              <div className="absolute top-2 right-2">
                <LikeButton listingId={listing.id} className="bg-white rounded-full p-1.5 shadow-md" />
              </div>
            )}
          </div>

          <div className="p-4">
            <div className="text-sm text-gray-500 mb-1">{listing.brand}</div>
            <h3 className="font-medium mb-2">{listing.title}</h3>
            <div className="flex items-center text-sm text-gray-600 mb-1">
              {isStore ? (
                <span className="font-medium text-gray-700">Sold by ClearanceWigs (official)</span>
              ) : (
                <>
                  <span>{listing.owner?.display_name || listing.owner?.full_name || 'Member'}</span>
                  {listing.owner?.rating > 0 && (
                    <>
                      <span className="mx-1">•</span>
                      <div className="flex items-center">
                        <Star className="h-4 w-4 text-yellow-400 fill-current" />
                        <span className="ml-1">{listing.owner.rating.toFixed(1)}</span>
                        <span className="ml-1">({listing.owner.review_count})</span>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-lg font-bold">${listing.price}</span>
              {listing.original_price ? (
                <span className="text-sm text-gray-400 line-through">${listing.original_price}</span>
              ) : (
                <span className="text-sm text-gray-600">{listing.condition}</span>
              )}
            </div>
            <div className="mt-4">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                isStore ? 'bg-rose-100 text-rose-800' :
                listing.type === 'sale' ? 'bg-green-100 text-green-800' :
                listing.type === 'trade' ? 'bg-blue-100 text-blue-800' :
                'bg-purple-100 text-purple-800'
              }`}>
                {isStore ? storeCtaLabel(listing) :
                 listing.type === 'sale' ? 'For Sale' :
                 listing.type === 'trade' ? 'For Trade' :
                 'Sale or Trade'}
              </span>
            </div>
          </div>
        </Link>
        );
      })}
    </div>
  );
}

export default SearchResults;